import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Linkedin, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PROFILE, ENGAGE_STEPS } from "../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const budgets = ["< $5K / mo", "$5K – $25K / mo", "$25K – $100K / mo", "$100K+ / mo"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thank you — your message is on its way. I'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email me directly.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/15 py-3 text-cream placeholder-cream/30 focus:border-gold focus:outline-none transition-colors";

  return (
    <section data-testid="contact-section" id="contact" className="relative py-28 lg:py-40 overflow-hidden">
      <div className="absolute top-0 right-0 h-[400px] w-[400px] rounded-full bg-gold/5 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow testid="contact-eyebrow">Book a Consult</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.05]">
              Let's build your <span className="italic text-gold-gradient">growth engine.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-cream/55 mt-6 text-base sm:text-lg leading-relaxed">
              Advisory, fractional CMO, or hands-on delivery — tell me where you want to take
              the brand and I'll tell you exactly how I'd get you there. I reply personally.
            </p>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-10 space-y-5 border-l border-white/10 pl-6">
              {ENGAGE_STEPS.map((s) => (
                <div key={s.n} data-testid={`engage-step-${s.n}`} className="flex gap-4">
                  <span className="font-serif text-gold text-lg leading-none pt-0.5">{s.n}</span>
                  <div>
                    <p className="text-cream font-medium">{s.t}</p>
                    <p className="text-cream/45 text-sm mt-0.5 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-10 space-y-4">
              <a data-testid="contact-email" href={`mailto:${PROFILE.email}`} className="flex items-center gap-4 text-cream/70 hover:text-gold transition-colors group">
                <span className="h-10 w-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-gold/50"><Mail size={16} /></span>
                {PROFILE.email}
              </a>
              <a data-testid="contact-phone" href={`tel:${PROFILE.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 text-cream/70 hover:text-gold transition-colors group">
                <span className="h-10 w-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-gold/50"><Phone size={16} /></span>
                {PROFILE.phone}
              </a>
              <a data-testid="contact-linkedin" href={PROFILE.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-4 text-cream/70 hover:text-gold transition-colors group">
                <span className="h-10 w-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-gold/50"><Linkedin size={16} /></span>
                LinkedIn
              </a>
              <div className="flex items-center gap-4 text-cream/70">
                <span className="h-10 w-10 rounded-full border border-white/10 flex items-center justify-center"><MapPin size={16} /></span>
                {PROFILE.location}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.2}>
            {sent ? (
              <div data-testid="contact-success" className="h-full min-h-[400px] flex flex-col items-center justify-center text-center border border-gold/20 rounded-3xl p-10 bg-charcoal-800/40">
                <CheckCircle2 className="text-gold mb-6" size={56} />
                <h3 className="font-serif text-3xl text-cream mb-3">Message received.</h3>
                <p className="text-cream/55 max-w-sm">Thank you for reaching out. I'll review your note and get back to you personally, usually within a day or two.</p>
                <button onClick={() => setSent(false)} className="mt-8 text-gold text-sm hover:underline">Send another message</button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="border border-white/10 rounded-3xl p-8 lg:p-12 bg-charcoal-800/40 backdrop-blur-sm">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-cream/40">Name *</label>
                    <input data-testid="contact-input-name" value={form.name} onChange={set("name")} placeholder="Your full name" className={field} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-cream/40">Email *</label>
                    <input data-testid="contact-input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={field} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-cream/40">Company</label>
                    <input data-testid="contact-input-company" value={form.company} onChange={set("company")} placeholder="Company / brand" className={field} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-cream/40">Monthly budget</label>
                    <select data-testid="contact-input-budget" value={form.budget} onChange={set("budget")} className={`${field} appearance-none cursor-pointer`}>
                      <option value="" className="bg-charcoal-800">Select a range</option>
                      {budgets.map((b) => <option key={b} value={b} className="bg-charcoal-800">{b}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-8">
                  <label className="text-xs uppercase tracking-[0.2em] text-cream/40">How can I help? *</label>
                  <textarea data-testid="contact-input-message" value={form.message} onChange={set("message")} rows={4} placeholder="Tell me about your brand and your goals…" className={`${field} resize-none`} />
                </div>
                <motion.button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="mt-10 w-full sm:w-auto px-10 py-4 rounded-full bg-gold text-charcoal font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Send message <ArrowRight size={18} /></>}
                </motion.button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
